import React, { useMemo, useState } from 'react';
import { useStore } from '../store-hybrid';
import { BrandLogo } from '../components/BrandLogo';

export const BrandsPage: React.FC = () => {
  const { brands, models, addBrand, addModel } = useStore();
  
  // Form states
  const [brandName, setBrandName] = useState('');
  const [brandGroup, setBrandGroup] = useState('');
  const [isAddingBrand, setIsAddingBrand] = useState(false);

  // Get unique groups for datalist
  const groups = useMemo(() => {
    const uniqueGroups = new Set<string>();
    brands.forEach(brand => brand.group && uniqueGroups.add(brand.group));
    return Array.from(uniqueGroups);
  }, [brands]);

  // Group brands by their group
  const groupedBrands = useMemo(() => {
    const grouped: { [key: string]: typeof brands } = {};
    
    brands.forEach(brand => {
      const group = brand.group || 'Other Brands';
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(brand);
    });
    
    return grouped;
  }, [brands]);

  const handleAddBrand = () => {
    if (!brandName.trim()) return;
    
    addBrand(brandName.trim(), brandGroup.trim() || undefined);
    setBrandName('');
    setBrandGroup('');
    setIsAddingBrand(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Brands & Models
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage car brands and their models
            </p>
          </div>
          
          <button
            onClick={() => setIsAddingBrand(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-xl hover:bg-primary-600 transition-colors font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add Brand
          </button>
        </div>

        {/* Add Brand Form */}
        {isAddingBrand && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add New Brand</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Name *
                </label>
                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="e.g., Volkswagen"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Group (optional)
                </label>
                <input
                  type="text"
                  value={brandGroup}
                  onChange={(e) => setBrandGroup(e.target.value)}
                  placeholder="e.g., Volkswagen Group"
                  list="groups"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
                />
                <datalist id="groups">
                  {groups.map((group) => (
                    <option key={group} value={group} />
                  ))}
                </datalist>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleAddBrand}
                disabled={!brandName.trim()}
                className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Add Brand
              </button>
              <button
                onClick={() => {
                  setIsAddingBrand(false);
                  setBrandName('');
                  setBrandGroup('');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Brands Grid by Group */}
        <div className="space-y-8">
          {Object.entries(groupedBrands).map(([groupName, groupBrands]) => (
            <div key={groupName}>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{groupName}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {groupBrands.map((brand) => (
                  <BrandCard
                    key={brand.id}
                    brand={brand}
                    modelCount={models.filter(m => m.brandId === brand.id).length}
                    onAddModel={(modelName) => addModel(brand.id, modelName)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {brands.length === 0 && (
          <div className="text-center py-12">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No brands yet</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">Get started by adding your first car brand.</p>
            <button
              onClick={() => setIsAddingBrand(true)}
              className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Add Your First Brand
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

interface BrandCardProps {
  brand: { id: string; name: string; group?: string };
  modelCount: number;
  onAddModel: (modelName: string) => void;
}

const BrandCard: React.FC<BrandCardProps> = ({ brand, modelCount, onAddModel }) => {
  const [modelName, setModelName] = useState('');
  const [isAddingModel, setIsAddingModel] = useState(false);
  const { models } = useStore();
  
  const brandModels = models.filter(m => m.brandId === brand.id);

  const handleAddModel = () => {
    if (!modelName.trim()) return;
    
    onAddModel(modelName.trim());
    setModelName('');
    setIsAddingModel(false);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      {/* Brand Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <BrandLogo brandId={brand.id} size="lg" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{brand.name}</h3>
            <p className="text-sm text-gray-500">{modelCount} models</p>
          </div>
        </div>
        
        <button
          onClick={() => setIsAddingModel(true)}
          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
          title="Add Model"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </button>
      </div>

      {/* Models List */}
      {brandModels.length > 0 && (
        <div className="space-y-2 mb-4">
          {brandModels.map((model) => (
            <div
              key={model.id}
              className="flex items-center justify-between px-3 py-2 bg-gray-50 dark:bg-gray-900 rounded-lg"
            >
              <span className="text-sm font-medium text-gray-700">{model.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Add Model Form */}
      {isAddingModel && (
        <div className="space-y-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <input
            type="text"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            placeholder="Model name (e.g., Golf)"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          />
          
          <div className="flex space-x-2">
            <button
              onClick={handleAddModel}
              disabled={!modelName.trim()}
              className="flex-1 px-3 py-2 bg-success-500 text-white text-sm font-medium rounded-lg hover:bg-success-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Add Model
            </button>
            <button
              onClick={() => {
                setIsAddingModel(false);
                setModelName('');
              }}
              className="px-3 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
